"use client"

import { CheckMini, SquareTwoStackMini } from "@medusajs/icons"
import { Text, Tooltip, clx } from "@medusajs/ui"
import { useState, type MouseEvent } from "react"

type CopySkuProps = {
  sku?: string | null
  className?: string
  "data-testid"?: string
}

const CopySku = ({
  sku,
  className,
  "data-testid": dataTestid,
}: CopySkuProps) => {
  const [copied, setCopied] = useState(false)

  if (!sku) {
    return null
  }

  const handleCopy = async (e: MouseEvent<HTMLButtonElement>) => {
    // Product previews/order cards are often wrapped in a link - don't navigate.
    e.preventDefault()
    e.stopPropagation()

    try {
      await navigator.clipboard.writeText(sku)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard API unavailable (e.g. insecure context) - nothing to fall back to.
    }
  }

  return (
    <span
      className={clx(
        "inline-flex items-center gap-x-1 max-w-full",
        className
      )}
    >
      <Text
        className="txt-small text-ui-fg-subtle truncate"
        data-testid={dataTestid}
        data-value={sku}
      >
        SKU: {sku}
      </Text>
      <Tooltip content={copied ? "Copied!" : "Copy SKU"}>
        <button
          type="button"
          onClick={handleCopy}
          className="text-ui-fg-muted hover:text-ui-fg-base transition-colors shrink-0"
          aria-label="Copy SKU"
          data-testid="copy-sku-button"
        >
          {copied ? (
            <CheckMini className="text-ui-fg-interactive" />
          ) : (
            <SquareTwoStackMini />
          )}
        </button>
      </Tooltip>
    </span>
  )
}

export default CopySku
